/**
 * Shared utilities for parsing and unwrapping tool arguments
 * Used by both client and extension to ensure consistent handling of wrapper objects
 */

/** Normalized HITL field descriptor (from Cortex arg_types_json). Backward compatible with legacy string type. */
export interface FieldDescriptor {
  type: string | { type: "select"; options: any[] };
  index?: number;
  description?: string;
  depends_on?: string;
  /** The field's value before this call, as the technician knows it (an update's "before -> after"). */
  before?: string;
  /** Autotask (or another platform) requires this field. */
  required?: boolean;
  /** Shown but not editable, e.g. the id of the record an update targets. */
  readonly?: boolean;
}

/**
 * Normalizes a field type from arg_types_json to a structured descriptor.
 * Handles legacy format (plain string or { type: "select", options: [] }) and new format
 * (object with type, index, description?, depends_on?).
 */
export function normalizeFieldDescriptor(descriptor: any): FieldDescriptor {
  if (descriptor == null) {
    return { type: "text", index: undefined, description: "" };
  }
  if (typeof descriptor === "string") {
    return { type: descriptor, index: undefined, description: "" };
  }
  if (typeof descriptor !== "object") {
    return { type: "text", index: undefined, description: "" };
  }
  // Support legacy select descriptor shape:
  // { type: "select", options: [...] }
  // In this case, preserve the full select object as the descriptor type so UI
  // branches can correctly render dropdown inputs instead of text fields.
  const isLegacySelectShape =
    descriptor.type === "select" &&
    Array.isArray(descriptor.options);

  const typeVal = isLegacySelectShape
    ? { type: "select", options: descriptor.options }
    : (descriptor.type ?? "text");
  return {
    type: typeVal,
    index: typeof descriptor.index === "number" ? descriptor.index : undefined,
    description: typeof descriptor.description === "string" ? descriptor.description : "",
    depends_on: typeof descriptor.depends_on === "string" ? descriptor.depends_on : undefined,
    before: typeof descriptor.before === "string" ? descriptor.before : undefined,
    required: descriptor.required === true ? true : undefined,
    readonly: descriptor.readonly === true ? true : undefined,
  };
}

/**
 * Returns the effective type for UI branching (bool, select, text, etc.).
 * For select, returns the object { type: "select", options }; otherwise the type string.
 */
export function getEffectiveType(descriptor: FieldDescriptor): string | { type: "select"; options: any[] } {
  const t = descriptor.type;
  if (t != null && typeof t === "object" && (t as any).type === "select" && Array.isArray((t as any).options)) {
    return t as { type: "select"; options: any[] };
  }
  return typeof t === "string" ? t : "text";
}

/**
 * Sorts [key, value] entries by the index from the field descriptor (same order as action definition).
 * Entries without an index sort last (use 999).
 */
export function sortEntriesByIndex(
  entries: [string, any][],
  getDescriptor: (key: string) => any
): [string, any][] {
  const normalized = entries.map(([key, value]) => {
    const desc = normalizeFieldDescriptor(getDescriptor(key));
    const index = typeof desc.index === "number" ? desc.index : 999;
    return { key, value, index };
  });
  normalized.sort((a, b) => a.index - b.index);
  return normalized.map(({ key, value }) => [key, value]);
}

/**
 * Parses nested arguments, handling both string and object formats.
 * Unwraps single-key wrapper objects at the top level.
 * 
 * @param args - Arguments as string or object
 * @returns Parsed and unwrapped arguments object
 */
export const parseNestedArgs = (args: string | object): Record<string, any> => {
  try {
    let parsed: Record<string, any> = {};
    
    if (typeof args === "string") {
      parsed = JSON.parse(args);
    } else if (typeof args === "object") {
      parsed = args as Record<string, any>;
    }

    return parsed;
  } catch (error) {
    return {};
  }
};

/**
 * Recursively unwraps all single-key wrapper objects from tool arguments.
 * A wrapper object is an object with exactly one key, where that key's value is also an object.
 * 
 * @param obj - Tool arguments object to unwrap
 * @returns Tool arguments with all wrappers recursively unwrapped
 */
export const unwrapToolArgumentWrappers = (obj: Record<string, any>): Record<string, any> => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return obj;
  }

  const result: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    // Check if this value is a single-key wrapper object
    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      const valueKeys = Object.keys(value);
      // If it's a single-key wrapper (one key, value is an object with keys)
      if (
        valueKeys.length === 1 &&
        value[valueKeys[0]] !== null &&
        typeof value[valueKeys[0]] === "object" &&
        !Array.isArray(value[valueKeys[0]]) &&
        Object.keys(value[valueKeys[0]]).length > 0
      ) {
        // Unwrap: merge the inner object's properties into result (recursively)
        const unwrapped = unwrapToolArgumentWrappers(value[valueKeys[0]]);
        Object.entries(unwrapped).forEach(([innerKey, innerValue]) => {
          if (innerValue !== null && innerValue !== undefined) {
            result[innerKey] = innerValue;
          }
        });
      } else {
        // Not a wrapper, recursively process nested objects
        result[key] = unwrapToolArgumentWrappers(value);
      }
    } else {
      // Primitive value or array, keep as is
      result[key] = value;
    }
  }
  
  return result;
};

/**
 * Formats an argument key for display in the HITL panel.
 * Splits snake_case and camelCase, then title-cases each word.
 * e.g. "use_custom_password" -> "Use Custom Password"
 * e.g. "usageLocation" -> "Usage Location"
 */
/**
 * The label a tool chose for a field, if any: `label` on a leaf descriptor ({ type, label }), or `_label` on the
 * type map of a nested object. Callers fall back to formatArgLabel(key).
 */
export function descriptorLabel(fieldType: any): string | undefined {
  if (!fieldType || typeof fieldType !== "object" || Array.isArray(fieldType)) return undefined;
  if (fieldType.type !== undefined && typeof fieldType.label === "string" && fieldType.label.trim()) return fieldType.label;
  if (typeof fieldType._label === "string" && fieldType._label.trim()) return fieldType._label;
  return undefined;
}

/** True when a leaf descriptor is marked readonly. */
export function descriptorReadonly(fieldType: any): boolean {
  return Boolean(fieldType && typeof fieldType === "object" && !Array.isArray(fieldType) && fieldType.readonly === true);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ZONED = /(Z|[+-]\d{2}:?\d{2})$/i;
const pad = (n: number) => String(n).padStart(2, "0");

/**
 * How a datetime field's value fills its input. A value with a zone (Z or an offset) is shown in the browser's
 * local time, so a technician never reads UTC digits as their own clock; a bare date gets a date picker; anything
 * else is shown as it is, as before.
 */
export function datetimeInput(raw: unknown): { type: "date" | "datetime-local"; value: string; zoned: boolean } {
  const text = String(raw ?? "").trim();
  if (DATE_ONLY.test(text)) return { type: "date", value: text, zoned: false };
  const parsed = ZONED.test(text) ? new Date(text) : null;
  if (parsed && !Number.isNaN(parsed.getTime())) {
    const local = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    return { type: "datetime-local", value: local, zoned: true };
  }
  return { type: "datetime-local", value: text.replace(" ", "T").replace("Z", "").slice(0, 16), zoned: false };
}

/** The value to store after the technician edits a datetime input: back to UTC when the original carried a zone. */
export function datetimeFromInput(input: string, original: unknown): string {
  if (!input || !datetimeInput(original).zoned) return input;
  const parsed = new Date(input); // a datetime-local string parses as local time
  return Number.isNaN(parsed.getTime()) ? input : parsed.toISOString().replace(/\.000Z$/, "Z");
}

/** The browser's time zone name, e.g. "Asia/Kolkata", for labelling local times. */
export function localTimeZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "local time";
  } catch {
    return "local time";
  }
}

export function formatArgLabel(key: string): string {
  if (!key) return key;
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  return spaced
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Checks if a field value is a wrapper object that should be skipped during rendering.
 * A wrapper object is an object with exactly one key, where that key's value is also an object.
 * 
 * @param value - The value to check
 * @returns True if the value is a wrapper object that should be skipped
 */
export const isWrapperObject = (value: any): boolean => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const valueKeys = Object.keys(value);
  // If it's a single-key wrapper (one key, value is an object with keys)
  if (valueKeys.length === 1) {
    const innerValue = value[valueKeys[0]];
    return innerValue !== null && 
           typeof innerValue === "object" && 
           !Array.isArray(innerValue) &&
           Object.keys(innerValue).length > 0;
  }

  return false;
};

